// The status glyph a Today appointment shows, chosen from the wire status code and the Today bucket.
// Look only: the label text and its data source are untouched (toWorklistCard).
import { pmStatusBucket } from '@/lib/pm-appointment-status';
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import type { BucketKey } from '../todays-appointments-view';

/** InSession / InProgress are folded onto CheckedIn by pmStatusBucket; they read as "in the room". */
const IN_ROOM = /^(insession|inprogress)$/;

/**
 * Has the visit's end passed? End on the wire, else start + minutesDuration; when neither parses, treat it as ended
 * (an unsigned draft with no clock to judge by is a note to sign).
 */
export function visitEnded(
  item: { start?: string | null; end?: string | null; minutesDuration?: number | null },
  nowMs: number,
): boolean {
  const parse = (iso?: string | null) => {
    if (!iso) return null;
    const t = Date.parse(iso);
    return Number.isNaN(t) ? null : t;
  };
  let endMs = parse(item.end);
  const startMs = parse(item.start);
  if (endMs == null && startMs != null && item.minutesDuration) endMs = startMs + item.minutesDuration * 60_000;
  return endMs == null ? true : nowMs >= endMs;
}

/**
 * `ended` only matters in the In-Progress bucket (an unsigned draft): after the visit's end it is a note to sign
 * (amber clock); while the visit runs it keeps the in-room look. Callers without a clock leave it off: ended.
 */
export function glyphKindForAppointment(
  statusCode: string | null | undefined,
  bucket?: BucketKey,
  ended: boolean = true,
): GlyphKind {
  if (bucket === 'in-progress') return ended ? 'clock' : 'full';
  const compact = (statusCode ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (IN_ROOM.test(compact)) return 'full';
  switch (pmStatusBucket(statusCode)) {
    case 'arrived':
    case 'checkedIn':
      return 'half';
    case 'fulfilled':
      return 'done';
    case 'noShow':
      return 'alert';
    default:
      // booked, confirmed, cancelled, waitlisted
      return 'empty';
  }
}

/**
 * The soft status chip's semantic for a Today glyph (design 21-10: AppointmentRow and the worklist swap StatusGlyph for
 * EncounterStatusChip): in the room and arrived or checked in → info, completed → success, no-show → error, booked,
 * confirmed and cancelled → neutral. An unsigned draft past its end (the PA's amber clock, not in the design's map)
 * stays the attention colour as a warning chip.
 */
export function chipSemanticForGlyph(kind: GlyphKind): 'info' | 'success' | 'error' | 'warning' | 'neutral' {
  switch (kind) {
    case 'full':
    case 'half':
      return 'info';
    case 'done':
      return 'success';
    case 'alert':
      return 'error';
    case 'clock':
    case 'up':
    case 'down':
      return 'warning';
    default:
      return 'neutral';
  }
}
